import {
  Box,
  VStack,
  HStack,
  Text,
  Skeleton,
  useColorModeValue,
  SimpleGrid,
  Badge,
  Flex,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Heading,
  IconButton,
  Button,
} from "@chakra-ui/react";
import React from "react";
import { FiFilter, FiMapPin, FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import { useDeleteLocation } from "../../services/configurationService";

const ConfigurationTable = ({
  configurationData = [],
  editLocationIsLoading,
  handleEdit,
  refetchGetLocations,
  config,
  getLocationsIsLoading,
}) => {
  const { deleteLocationIsLoading, deleteLocationPayload } = useDeleteLocation(
    (res) => refetchGetLocations()
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bgColor = useColorModeValue("white", "gray.800");

  const handleDelete = (id) => {
    deleteLocationPayload(id);
  };

  if (getLocationsIsLoading) {
    return (
      <Box p={6}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Box key={i} bg="white" p={6} borderRadius="16px" shadow="sm" border="1px solid" borderColor="gray.100">
              <HStack spacing={4} mb={4}>
                <Skeleton height="40px" width="40px" borderRadius="full" />
                <VStack spacing={2} align="start" flex={1}>
                  <Skeleton height="16px" width="60%" />
                  <Skeleton height="14px" width="40%" />
                </VStack>
              </HStack>
              <VStack spacing={2} align="start">
                <Skeleton height="14px" width="100%" />
                <Skeleton height="14px" width="80%" />
                <Skeleton height="14px" width="60%" />
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  if (!getLocationsIsLoading && !configurationData?.length) {
    return (
      <Box p={8} textAlign="center">
        <VStack spacing={4}>
          <Box
            p={4}
            borderRadius="50%"
            bg="gray.100"
            color="gray.500"
            fontSize="2xl"
          >
            ⚙️
          </Box>
          <VStack spacing={2}>
            <Text fontSize="lg" fontWeight="600" color="gray.700">
              No {config} Data
            </Text>
            <Text color="gray.500" fontSize="sm">
              No {config.toLowerCase()} records found. Add your first {config.toLowerCase()} to get started.
            </Text>
          </VStack>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      {/* Table Header */}
      <Flex 
        justifyContent="space-between" 
        alignItems="center" 
        p={6} 
        borderBottom="1px solid" 
        borderColor="gray.200"
        bg={bgColor}
      >
        <VStack align="start" spacing={1}>
          <Heading size="md" color="gray.900" fontWeight="600">
            {config} Management
          </Heading>
          <Text color="gray.600" fontSize="sm">
            {configurationData.length} {config.toLowerCase()} found
          </Text>
        </VStack>
        <HStack spacing={3}>
          <IconButton
            icon={<FiFilter />}
            variant="ghost"
            onClick={onOpen}
            aria-label="Filter configurations"
            color="gray.600"
            _hover={{ bg: "gray.100" }}
          />
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            variant="outline"
            size="sm"
            borderRadius="8px"
          >
            Add {config}
          </Button>
        </HStack>
      </Flex>

      {/* Configuration Cards */}
      <VStack spacing={4} p={6} align="stretch">
        {configurationData?.map((item) => (
          <ConfigurationCard
            key={item?.id}
            name={item?.full_name}
            ipAddress={item?.ip_address}
            location={item?.address}
            id={item?.id}
            onEdit={() => handleEdit(item)}
            onDelete={() => handleDelete(item?.id)}
            editLoading={editLocationIsLoading}
            deleteLoading={deleteLocationIsLoading}
            config={config}
          />
        ))}
      </VStack>

      {/* Filter Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Filter {config}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text color="gray.600">Filter options coming soon...</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Configuration Card Component
const ConfigurationCard = ({ id, name, ipAddress, location, onEdit, onDelete, editLoading, deleteLoading, config }) => {
  return (
    <Box
      bg="white"
      p={6}
      borderRadius="16px"
      shadow="sm"
      border="1px solid"
      borderColor="gray.200"
      _hover={{ shadow: "md" }}
      transition="all 0.2s"
    >
      <HStack spacing={4} align="center">
        <Box
          p={3}
          borderRadius="12px"
          bg="blue.100"
          color="blue.600"
        >
          <FiMapPin size={20} />
        </Box>
        <VStack spacing={1} align="start" flex={1}>
          <Text fontWeight="600" color="gray.900" fontSize="md">
            {location || name}
          </Text>
          <Text color="gray.600" fontSize="sm">
            ID: {id}
            {ipAddress && ` • IP: ${ipAddress}`}
          </Text>
        </VStack>
        <HStack spacing={2}>
          <IconButton
            icon={<FiEdit />}
            variant="ghost"
            onClick={onEdit}
            aria-label="Edit configuration"
            color="blue.600"
            _hover={{ bg: "blue.50" }}
            isLoading={editLoading}
            size="sm"
          />
          <IconButton
            icon={<FiTrash2 />}
            variant="ghost"
            onClick={onDelete}
            aria-label="Delete configuration"
            color="red.600"
            _hover={{ bg: "red.50" }}
            isLoading={deleteLoading}
            size="sm"
          />
        </HStack>
      </HStack>
    </Box>
  );
};

export default ConfigurationTable;
